import React, {createRef, useEffect, useRef, useState} from 'react'
import createPlotlyComponent from 'react-plotly.js/factory';
import Utils from '../../helpers/Utility';
import _ from 'lodash';
import {Button} from "antd";
import userService from '../../services/userService';
import momentTimezone from 'moment-timezone';
import { pinTypeAxisTitle } from '../../helpers/graphSensorUnit';

const Plotly = window.Plotly;
const Plot = createPlotlyComponent(Plotly);

interface GraphProps {
    plots: any[]
    fieldTestOfflineDuration?: any;
    showThresholds1:boolean;
    showThresholds2:boolean;
    sensorGroup?: any;
    timezone?: string | null;
}

const Graph = (props: GraphProps) => {

    const graphContainerRef: any = createRef()
    const [data, setData] = useState<any[]>([]);
    const [sameUnits, setSameUnits] = useState(false)
    useEffect(() => {
        prepData()
    }, [props.plots,props.showThresholds1,props.showThresholds2,props.timezone])
    useEffect(() => {
        if(data.length>0)
        setLayout(() => layouts(data.filter(x => !x.threshold)))
    }, [data])
    const [layout, setLayout] = useState({});
    const layouts = (lines: any) => {
        const finiteMins = lines
            .map((x: any) => Number(x.min))
            .filter((x: number) => Number.isFinite(x))
        const finiteMaxs = lines
            .map((x: any) => Number(x.max))
            .filter((x: number) => Number.isFinite(x))
        const min: number | null = finiteMins.length ? (_.min(finiteMins) as number) : null
        const max: number | null = finiteMaxs.length ? (_.max(finiteMaxs) as number) : null
        let result = {
            width: graphContainerRef?.current?.offsetWidth,
            // title: title,
            legend: {
                x: 0,
                y: 1.05,
                orientation: 'h',
            },
            autosize: true,
            xaxis: {
                type: "date",
                tickformat: '%I:%M:%S %p\n%B %d, %Y',
                uirevision: 'time',
                // linewidth: 1,
                zerolinecolor: 'red', // Change the color of the X-axis origin
                zerolinewidth: 2,     // Change the width of the X-axis origin line
            },
            yaxis: {
                type: 'linear',
                title: pinTypeAxisTitle(lines[0]?.sensor_type),
                titlefont: {color: 'black'},
                tickfont: {color: 'black'},
                linewidth: 1,

            },

        } as any
        if(lines[1]){
            result['yaxis2'] = {
                type: 'linear',
                title: pinTypeAxisTitle(lines[1]?.sensor_type),
                titlefont: {color: 'black'},
                tickfont: {color: 'black'},
                overlaying: 'y',
                side: 'right',
                linewidth: 1,
            }
        }
        if (lines[0]?.sensor_type === 'switch') {
            result.yaxis[`tickvals`] = [0, 1]
        }
        if (lines[1]?.sensor_type === 'switch') {
            result.yaxis2[`tickvals`] = [0, 1]
        }
        if (lines[0]?.sensor_type === lines[1]?.sensor_type) {
            result.yaxis['anchor'] = 'x'
            if (result.yaxis2) {
                result.yaxis2['anchor'] = 'x'
                result.yaxis2['matches'] = 'y'
            }
            if (min !== null && max !== null) {
                const spread = Math.abs(max - min)
                const gap = spread === 0 ? Math.max(Math.abs(max) * 0.05, 1) : spread * 0.1
                result.yaxis['range'] = [min - gap, max + gap]
            }
            setSameUnits(true)
        } else setSameUnits(false)
        const x = lines[0]?.x
        const xMin = x?.[0],
            xMax = x?.[x?.length - 1]

        if (xMin && xMax)
            result.xaxis.range = [xMin - 86400, xMax + 86400]
        else
            result.xaxis.autorange = true

        return result
    }
    const addThresholdsHighlight = (line: any) => {
        const maxLine = {
            ...line, y: _.times(line.y.length, _.constant(line.thresholds[1])),
            line: {
                color: 'red',
                width: 2
            }, name: `${line.name} Max.`, mode: 'lines', threshold: true
        }
        const minLine = {
            ...line, y: _.times(line.y.length, _.constant(line.thresholds[0])),
            line: {
                color: 'orange',
                width: 2
            }, name: `${line.name} Min.`, mode: 'lines', threshold: true
        }
        return [maxLine, minLine]
    }
    const convertTimestampsToTimezone = (timestamps: any[], timezone: string | null): any[] => {
        if (!timezone || !timestamps || timestamps.length === 0) {
            return timestamps
        }
        
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        
        // Convert each timestamp to show sensorbox timezone time when displayed
        return timestamps.map((ts: any) => {
            if (!ts) return ts
            
            // Convert UTC timestamp to sensorbox timezone
            const sensorboxTime = momentTimezone.tz(ts, timezone)
            
            // Get the time components in sensorbox timezone
            const year = sensorboxTime.year()
            const month = sensorboxTime.month()
            const date = sensorboxTime.date()
            const hour = sensorboxTime.hour()
            const minute = sensorboxTime.minute()
            const second = sensorboxTime.second()
            const millisecond = sensorboxTime.millisecond()
            
            const boxTime = momentTimezone.tz({
                year, month, date, hour, minute, second, millisecond
            }, browserTimezone)
            
            return boxTime.valueOf()
        })
    }

    const prepData = () => {
        let thresholds: any[] = []
        let lines = props.plots.map((line: any, index: number) => {
            const processedLine = {...line}
            processedLine.yaxis = 'y'
            if (index === 1 && line.lineId === 1)
                processedLine.yaxis = 'y2'
            
            // Convert timestamps to sensorbox timezone if timezone is provided
            if (props.timezone && processedLine.x) {
                processedLine.x = convertTimestampsToTimezone(processedLine.x, props.timezone)
            }
            
            if (line.showThresholds) {
                const thresholdLines = addThresholdsHighlight(processedLine)
                // Also convert threshold line timestamps
                thresholdLines.forEach((thresholdLine: any) => {
                    if (props.timezone && thresholdLine.x) {
                        thresholdLine.x = convertTimestampsToTimezone(thresholdLine.x, props.timezone)
                    }
                })
                thresholds = [...thresholds, ...thresholdLines]
            }
            return processedLine
        })
        setData(() => [...lines, ...thresholds])
    }

    const handleCopy = async () => {
        try {
            const plotElement = document.querySelector('.js-plotly-plot');
            if (!plotElement) {
                alert('Plot not found!');
                return;
            }

            // Use Plotly's built-in toImage method
            const imageData = await Plotly.toImage(plotElement as any, {
                format: 'png',
                height: 400,
                width: 1200,
                scale: 3
            });

            // Convert base64 to Blob
            const response = await fetch(imageData);
            const blob = await response.blob();

            // Copy image to clipboard
            await navigator.clipboard.write([
                new ClipboardItem({
                    'image/png': blob,
                }),
            ]);
        } catch (error) {
            console.error('Clipboard copy failed:', error);
            alert('Copy failed');
        }
    };

    const handleEmailPlot = async () => {
        try {
            const plotElement = document.querySelector('.js-plotly-plot');
            if (!plotElement) {
                alert('Plot not found!');
                return;
            }

            if (!props.sensorGroup?._id) {
                alert('Sensor group information not available');
                return;
            }

            let users = await userService.getUsersBySensorGroup(props.sensorGroup._id as any)
            
            // Construct email parameters
            let to_email = users.contractorDetails?.map((u: any) => u.email).filter(Boolean).join(', ') || '';
            let cc_email = users.consumerDetails?.map((u: any) => u.email).filter(Boolean).join(', ') || '';
            const sensorGroupName = props.sensorGroup?.sensor_group_name || props.sensorGroup?.name || 'Sensor Group';
            const alias = props.sensorGroup?.alias || '';
            const subject = encodeURIComponent(`${sensorGroupName}${alias ? ` - ${alias}` : ''}`);
            const body = encodeURIComponent("Hi,\n\nPlease find the chart attached below.\n\n--\nBest Regards,\nMechasense Team");

            // Since mailto does not support full HTML bodies, open in new window instead
            window.open(`mailto:${to_email}?cc=${cc_email}&subject=${subject}&body=${body}`);
        } catch (error) {
            console.error('Email plot failed:', error);
            alert('Failed to email plot.');
        }
    };

    const customCopyIcon = {
        width: 524,
        height: 524,
        path: "M408 112H184c-13.2 0-24 10.8-24 24v32h48v-16h176v176h-16v48h32c13.2 0 24-10.8 24-24V136c0-13.2-10.8-24-24-24zM328 192H104c-13.2 0-24 10.8-24 24v208c0 13.2 10.8 24 24 24h224c13.2 0 24-10.8 24-24V216c0-13.2-10.8-24-24-24zM320 416H112V224h208v192z"
    };

    const customMailIcon = {
        width: 512,
        height: 512,
        path: "M467 61H45C20.2 61 0 81.2 0 106v300c0 24.8 20.2 45 45 45h422c24.8 0 45-20.2 45-45V106c0-24.8-20.2-45-45-45zM256 295L45 151v-5h422v5L256 295z"
    };

    let cycleInfo = {} as any

    let tstatWSwitch: any = props.plots?.find((sensor: any) => (sensor?.representation === 'tstat_w'))

    let isFieldTest = props.plots?.findIndex(x => x.category === 'fieldtest') !== -1

    if (tstatWSwitch && isFieldTest) {

        let startTime = null as any;
        let prevValue = null as any;
        let prevTime = null as any;
        let timeCycleInms = null as any;
        let cycle = 0;
        let offlineDuration = props.fieldTestOfflineDuration ? Number(props.fieldTestOfflineDuration) : 10;

        tstatWSwitch.y?.forEach((currentVal: any, index: any) => {

            if (index >= 0) {
                if (currentVal === 1 && prevValue === 0) {
                    cycle += 1;
                    startTime = tstatWSwitch.x?.length > 0 ? tstatWSwitch.x[index] : 0
                } else if (currentVal === 1 && prevValue === 1) {

                    let currentTime = tstatWSwitch.x?.length > 0 ? tstatWSwitch.x[index] : 0
                    let timeDiffOfCurrentAndPrevData = (currentTime - prevTime)
                    if (timeDiffOfCurrentAndPrevData >= offlineDuration * 60 * 1000) {

                        if (startTime) {
                            let timeDiffOfCycle = (tstatWSwitch.x?.length > 0 ? (tstatWSwitch.x[index] - startTime) : 0);
                            timeCycleInms += timeDiffOfCycle - timeDiffOfCurrentAndPrevData
                        }

                        cycle += 1;
                        startTime = tstatWSwitch.x?.length > 0 ? tstatWSwitch.x[index] : 0
                    }

                } else if (currentVal === 0 && prevValue === 1) {

                    let currentTime = tstatWSwitch.x?.length > 0 ? tstatWSwitch.x[index] : 0
                    let timeDiffOfCurrentAndPrevData = (currentTime - prevTime)

                    if (startTime) {
                        let timeDiffOfCycle = (tstatWSwitch.x?.length > 0 ? (tstatWSwitch.x[index] - startTime) : 0);
                        timeCycleInms += (timeDiffOfCurrentAndPrevData >= 10 * 60 * 1000 ? (timeDiffOfCycle - timeDiffOfCurrentAndPrevData) : timeDiffOfCycle)
                    }

                }

                prevValue = currentVal
                prevTime = (tstatWSwitch.x?.length > 0 ? tstatWSwitch.x[index] : 0)
            }

        })

        if ((tstatWSwitch.y[tstatWSwitch.y?.length - 1]) === 1) {
            cycle = cycle > 0 ? (cycle - 1) : cycle
        }

        cycleInfo['noOfCycles'] = cycle;
        cycleInfo['durationOfCycles'] = Utils.durationAsString(timeCycleInms); // in ms
    }

    return (
        <div
            className="graph-container"
            hidden={props.plots.length === 0}
            ref={graphContainerRef}
        >
            {sameUnits && <Button
                size='small'
                onClick={() => {
                    prepData()
                }}>
                Align Axes
            </Button>}
            {(tstatWSwitch && isFieldTest) &&
                <div className='d-flex float-left font-w-600'>
                    <div className='flex-fill'>
                        <p>{cycleInfo ? `No Of Cycles For TSTAT Heat 'W' : ${cycleInfo?.noOfCycles}` : ''}</p>
                    </div>
                    <div className='flex-auto ml-4'>
                        <p>{cycleInfo ? `Total Time Of Cycles For TSTAT Heat 'W' : ${cycleInfo?.durationOfCycles}` : ''}</p>
                    </div>
                </div>
            }

            <Plot
                data={data}
                layout={layout}
                config={{
                    scrollZoom: false,
                    displaylogo: false,
                    toImageButtonOptions: {
                        format: 'svg', // one of png, svg, jpeg, webp
                        filename: 'custom_image',
                        height: 500,
                        width: 700,
                        scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
                    },
                    modeBarButtonsToAdd: [{
                        title: 'Copy plot',
                        name: 'Copy plot',
                        icon: customCopyIcon,
                        click: handleCopy,
                    },{
                        title: 'Email plot',
                        name: 'Email plot',
                        icon: customMailIcon,
                        click: handleEmailPlot,
                    }],
                    responsive: true,
                    displayModeBar: true,
                }}
            />
        </div>
    );

}

export default Graph